/**
 * 포맷팅 관련 공통 유틸리티 함수들
 */

import { formatCompactNumber } from 'src/utils/format/format-compact-number';

/** `new Date(...)` 가 그대로 받는 것들 */
type DateInput = Date | string | number;

/**
 * `joined`(가입일) 원본처럼 **모양을 보장할 수 없는 것**을 읽을 때 쓰는 오리 타입.
 * `src/sections/bookmark/utils/bookmark-firestore-time.ts` 의 `TimestampLike` 와 같은
 * 관용구다 — `unknown` 을 좁히는 곳이 한 군데뿐이라 여기 지역 타입으로 둔다.
 */
type FirestoreTimestampLike = {
  toDate?: () => Date;
  seconds?: unknown;
  _seconds?: unknown;
};

/**
 * 숫자를 읽기 쉬운 형태로 포맷팅
 * @param {number} num - 포맷팅할 숫자
 * @returns {string} 포맷팅된 문자열
 * 
 * @example
 * formatNumber(1234) // "1.2K"
 * formatNumber(1234567) // "1.2M"
 * formatNumber(999) // "999"
 */
export const formatNumber = (num: number): string =>
  formatCompactNumber(num, {
    // ⚠️ 이 자리만 «못 읽으면 0» 이고, 1000 미만에 천단위 구분이 없으며,
    //    ±Infinity 를 안 막는다(`'InfinityM'`). 셋 다 이 자리의 계약이라 바꾸지 마라.
    fallback: '0',
    invalidWhen: 'nan',
    nullishAsZero: false,
    nonPositive: 'keep',
    below: 'plain',
  });

/**
 * `formatNumber`와 동일하되 0일 때만 대시로 표시.
 *
 * 상단 오버뷰 카드에서 "조회수 0인 플랫폼"을 0이 아니라 `-`로 보여주는 기존 표기 규칙.
 *
 * @param {number} num
 * @returns {string}
 *
 * @example
 * formatNumberOrDash(1234) // "1.2K"
 * formatNumberOrDash(0)    // "-"
 */
export const formatNumberOrDash = (num: number): string => {
  const formatted = formatNumber(num);
  return formatted === '0' ? '-' : formatted;
};

/**
 * 숫자에 세 자리마다 콤마(,)를 추가하여 포맷팅
 * @param {number|string} num - 포맷팅할 숫자
 * @param {number} decimals - 소수 자릿수 (기본값: 0, 소수 이하 반올림)
 * @returns {string} 콤마가 추가된 문자열
 *
 * @example
 * formatNumberWithComma(1234567)    // "1,234,567"
 * formatNumberWithComma(1234.5678, 2) // "1,234.57"
 * formatNumberWithComma(999)         // "999"
 */
export const formatNumberWithComma = (num: number | string, decimals: number = 0): string => {
  if (num === null || num === undefined) return '0';

  const number = Number(num);
  if (Number.isNaN(number)) return '0';

  if (decimals > 0) {
    const fixed = number.toFixed(decimals);
    const [intPart, fracPart] = fixed.split('.');
    const intWithComma = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fracPart !== undefined ? `${intWithComma}.${fracPart}` : intWithComma;
  }

  const intPart = Math.round(number).toString();
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * 금액을 억원/만원 단위로 포맷팅
 * @param {number} num - 금액 (원)
 * @returns {string} 포맷팅된 문자열 (예: "3.5억원", "2500만원")
 */
export const formatCurrency = (num: number): string => {
  if (num === null || num === undefined) return '0원';
  const number = Number(num);
  if (isNaN(number) || number === 0) return '0원';
  if (number >= 100000000) return `${(number / 100000000).toFixed(1)}억원`;
  if (number >= 10000) return `${(number / 10000).toFixed(0)}만원`;
  return `${number.toLocaleString()}원`;
};

/**
 * 숫자를 반올림하여 문자열로 반환
 * @param {number} num - 반올림할 숫자
 * @param {number} decimals - 소수 자릿수 (기본값: 0)
 * @returns {string} 반올림된 문자열
 * @example
 * formatRounded(12.7) // "13"
 * formatRounded(12.34, 1) // "12.3"
 */
export const formatRounded = (num: number, decimals: number = 0): string => {
  if (num === null || num === undefined) return '0';
  const n = Number(num);
  if (Number.isNaN(n)) return '0';
  if (decimals === 0) return String(Math.round(n));
  return n.toFixed(decimals);
};

/**
 * 타임스탬프를 통일된 날짜 형태로 포맷팅
 *
 * ⚠️ `Date` 인스턴스도 받는다 — 본문에 `timestamp instanceof Date` 갈래가 있으므로
 * `@param` 을 `number|string` 으로 좁히지 마라. `.ts` 호출부가 막힌다.
 *
 * @param {number|string|Date|null|undefined} timestamp - Unix timestamp, ISO string,
 *   "YYYY-MM-DD"·"YYYYMMDD" 형태, 또는 Date 인스턴스
 * @returns {string} 포맷팅된 날짜 문자열 (YYYY-MM-DD HH:mm 형태 또는 YYYY-MM-DD)
 * 
 * @example
 * formatTimestamp(1752249504) // "2025-07-12 10:30"
 * formatTimestamp("2025-07-12T10:30:00Z") // "2025-07-12 10:30"
 * formatTimestamp("2025-07-12") // "2025-07-12"
 */
export const formatTimestamp = (timestamp: number | string | Date | null | undefined): string => {
  if (timestamp === null || timestamp === undefined || timestamp === '') return '';
  
  let date: Date | undefined;
  let hasTime = false;

  // 8-digit compact date string: YYYYMMDD
  if (typeof timestamp === 'string' && /^\d{8}$/.test(timestamp.trim())) {
    const s = timestamp.trim();
    const year = Number(s.slice(0, 4));
    const month = Number(s.slice(4, 6));
    const day = Number(s.slice(6, 8));
    date = new Date(year, month - 1, day, 0, 0, 0);
    hasTime = false;
  }
  // Number or numeric-like string (epoch seconds/millis)
  else if (typeof timestamp === 'number' || (typeof timestamp === 'string' && /^\d+$/.test(timestamp.trim()))) {
    const raw = Number(timestamp);
    if (Number.isNaN(raw)) return '';
    // Detect seconds vs milliseconds (|raw| < 1e12 → seconds)
    const ms = Math.abs(raw) < 1e12 ? raw * 1000 : raw;
    date = new Date(ms);
    hasTime = true;
  }
  // Exact date only string: YYYY-MM-DD
  else if (typeof timestamp === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(timestamp.trim())) {
    date = new Date(timestamp.trim() + 'T00:00:00');
    hasTime = false;
  }
  // Flexible date string: YYYY-M-D or YYYY-MM-D or YYYY-M-DD, with optional time HH:mm(:ss)?
  else if (typeof timestamp === 'string' && /^\d{4}-\d{1,2}-\d{1,2}(?:[ T]\d{1,2}:\d{2}(?::\d{2})?)?$/.test(timestamp.trim())) {
    const trimmed = timestamp.trim().replace(' ', 'T');
    const match = trimmed.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:T(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const hours = match[4] !== undefined ? Number(match[4]) : 0;
      const minutes = match[5] !== undefined ? Number(match[5]) : 0;
      const seconds = match[6] !== undefined ? Number(match[6]) : 0;
      date = new Date(year, month - 1, day, hours, minutes, seconds);
      hasTime = match[4] !== undefined; // time exists
    }
  }
  // Date with time string: YYYY-MM-DD HH:mm(:ss)? or ISO with T
  else if (typeof timestamp === 'string' && /^(\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2})?)/.test(timestamp.trim())) {
    const normalized = timestamp.trim().replace(' ', 'T');
    date = new Date(normalized);
    hasTime = true;
  }
  // Generic string (ISO or parseable)
  else if (typeof timestamp === 'string') {
    const trimmed = timestamp.trim();
    date = new Date(trimmed);
    hasTime = trimmed.includes('T') || /\d{2}:\d{2}/.test(trimmed);
  }
  // Date instance
  else if (timestamp instanceof Date) {
    date = timestamp;
    hasTime = true;
  } else {
    return '';
  }
  
  // Invalid date guard
  if (!date || Number.isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  if (hasTime) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }
  
  return `${year}-${month}-${day}`;
};

/**
 * 날짜를 YYYY-MM-DD 형태로 포맷팅
 * @param {Date|string|number} date - 날짜
 * @returns {string} YYYY-MM-DD 형태의 문자열
 * 
 * @example
 * formatDate(new Date()) // "2025-01-15"
 */
const formatDate = (date: DateInput): string => {
  if (!date) return '';
  
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * 인플루언서 프로필 `joined`(가입일)을 YYYY-MM-DD로 표시
 * Firestore Timestamp·{seconds}·epoch·ISO 등 지원. 파싱 불가·플레이스홀더("-")는 빈 문자열.
 *
 * @param {unknown} value - joined 원본
 * @returns {string} YYYY-MM-DD 또는 ''
 */
export const formatInfluencerJoinedDate = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';

  if (typeof value === 'string') {
    const t = value.trim();
    if (!t || t === '-' || /^invalid/i.test(t)) return '';
  }

  const like = value as FirestoreTimestampLike;

  try {
    if (typeof like?.toDate === 'function') {
      const d = like.toDate();
      if (Number.isNaN(d.getTime())) return '';
      return formatDate(d);
    }

    if (value && typeof value === 'object' && !(value instanceof Date)) {
      const sec = typeof like.seconds === 'number'
        ? like.seconds
        : (typeof like._seconds === 'number' ? like._seconds : null);
      if (sec != null) {
        const d = new Date(sec * 1000);
        if (Number.isNaN(d.getTime())) return '';
        return formatDate(d);
      }
      return '';
    }

    if (value instanceof Date) {
      return formatDate(value);
    }

    const formatted = formatTimestamp(value as number | string | Date);
    if (!formatted) return '';
    return formatted.slice(0, 10);
  } catch {
    return '';
  }
};

/**
 * 국가/마켓 코드 정규화 (비교·필터용)
 * @param {string} value - 코드 문자열
 * @returns {string} trim + 대문자
 * @example
 * normalizeMarketCode(' kr ') // "KR"
 */
export const normalizeMarketCode = (value: string | null | undefined): string => {
  if (!value) return '';
  return String(value).trim().toUpperCase();
};

/**
 * 라벨 정규화 (성분/피부고민 등 비교·집계용, 공백 제거)
 * @param {string} value - 라벨 문자열
 * @returns {string} trim + 소문자 + 연속 공백 제거
 * @example
 * normalizeLabel('  Niacinamide  ') // "niacinamide"
 */
export const normalizeLabel = (value: string): string => {
  if (!value) return '';
  return String(value).trim().toLowerCase().replace(/\s+/g, '');
};

/**
 * 해시태그 문자열에서 앞쪽 # 제거
 * @param {string} hashtag - 해시태그 문자열 (예: "#키워드", "##tag")
 * @returns {string} # 제거된 문자열
 * @example
 * normalizeHashtag('#키워드') // "키워드"
 */
export const normalizeHashtag = (hashtag: string): string => {
  if (!hashtag || typeof hashtag !== 'string') return '';
  return hashtag.replace(/^#+/, '').trim();
};

/**
 * HTML 태그를 제거하고 순수 텍스트만 반환하는 유틸리티 함수
 * @param {string} html - HTML 문자열
 * @returns {string} HTML 태그가 제거된 순수 텍스트
 */
function stripHtml(html: unknown): string {
  if (!html || typeof html !== 'string') {
    return '';
  }
  
  // HTML 태그 제거
  return html.replace(/<[^>]*>/g, '');
}

export default stripHtml; 